// Server-side URL-query parsing for the shared search layout. Extracted from
// the /Buscar page so preset search pages can reuse the exact same parsing in
// their own getServerSideProps. Lives outside src/pages/ on purpose: non-page
// files in there leak into the filesystem-scanned sitemap.

import { DEFAULT_COMPANY } from "@/pages/Buscar/useSearchState";

// Range syntax: "min-max" → [min, max]. "min-" → [min]. "-max" → [0, max]. Anything else → [].
export const parseRange = (raw) => {
    if (typeof raw !== "string" || !raw.includes("-")) return [];
    const [minStr, maxStr] = raw.split("-");
    const min = minStr === "" ? 0 : Number(minStr);
    const max = maxStr === "" ? null : Number(maxStr);
    if (max === null) return Number.isFinite(min) ? [min] : [];
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
    return [min, max];
};

const parseSingleNumber = (raw) => {
    if (raw === undefined || raw === null || raw === "") return [];
    const n = Number(raw);
    return Number.isFinite(n) ? [n] : [];
};

// Bedrooms-like keys accept either a single value (compact, >= semantic) or a
// range "n-m" (exact / BETWEEN semantic, normally written as "n-n" for "exactly n").
export const parseSingleOrRange = (raw) => {
    if (typeof raw !== "string" || raw === "") return [];
    if (raw.includes("-")) return parseRange(raw);
    return parseSingleNumber(raw);
};

// URL contract: polygon vertices encoded as `lat,lng;lat,lng;...`.
// Returns the parsed vertex array (≥3 vertices required for a usable polygon),
// or [] on any parse failure (drops the polygon silently so the rest of the
// URL still hydrates).
export const parsePolygonCoords = (raw) => {
    if (typeof raw !== "string" || raw === "") return [];
    const verts = [];
    for (const pair of raw.split(";")) {
        const [latStr, lngStr] = pair.split(",");
        const lat = Number(latStr);
        const lng = Number(lngStr);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return [];
        verts.push({ lat, lng });
    }
    return verts.length >= 3 ? verts : [];
};

// Initial polygons stack precedence + origin tag:
//   1. `polygon=` URL key (repeatable) → manually-drawn polygons,
//      origin 'draw'. One entry per repeated key.
//   2. If no `polygon=` entries AND `q + lat + lng + radio` are present →
//      single place-derived circle, origin 'place'. Preserves the
//      homepage→Buscar route behavior.
//   3. The page's default boundary entries (origin 'floor'), when the page
//      config provides them — deep links therefore override the floor.
//   4. Empty array.
// The origin tag drives downstream semantics (apiBody's grace buffer:
// place-derived circles get padding, drawn shapes are exact; floor entries
// never serialize to the URL). The URL carries origin implicitly by which
// branch hydrates.
export const derivePolygons = (parsed, query, defaultPolygons = null) => {
    // Next's getServerSideProps gives us string | string[] for repeated keys.
    const raw = query.polygon;
    const rawList = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const drawn = rawList
        .map(parsePolygonCoords)
        .filter((verts) => verts.length >= 3)
        .map((verts) => ({ shape: "polygon", coords: verts, origin: "draw" }));
    if (drawn.length > 0) return drawn;
    if (
        parsed.q &&
        Number.isFinite(parsed.latitude) &&
        Number.isFinite(parsed.longitude) &&
        Number.isFinite(parsed.radius)
    ) {
        return [
            {
                shape: "circle",
                coords: {
                    center: { lat: parsed.latitude, lng: parsed.longitude },
                    radius: parsed.radius,
                },
                origin: "place",
            },
        ];
    }
    if (defaultPolygons?.length) return defaultPolygons;
    return [];
};

// Translate a search-page registry entry into the parseSearchQuery overlay:
// the page's boundary rings become default polygon entries (origin "floor" —
// never serialized to the URL, replaced wholesale by any user boundary), and
// filter presets/company pass through as-is. Lives here (not in the page
// factory) because both the factory's GSSP and BuscarLayout's client-side
// defaults need it, and the factory imports BuscarLayout.
export const pageDefaultsFrom = (config) => {
    const defaults = { ...(config.defaults ?? {}) };
    if (config.company) defaults.company = config.company;
    if (config.floor) {
        defaults.polygons = config.floor.polygons.map((coords) => ({
            shape: "polygon",
            coords,
            origin: "floor",
        }));
    }
    return defaults;
};

// Translate Spanish URL keys → English API/state shape that BuscarLayout consumes.
// Default-stripping happens client-side on URL writes; SSR just reads what's there.
//
// `pageDefaults` (optional) lets a preset search page overlay its own defaults
// where the URL is silent — the URL always wins where present. Supported keys:
// `company` (replaces the all-companies default), `polygons` (default boundary
// entries, see derivePolygons), `type` and `operation` (filter presets).
// Null/absent pageDefaults ⇒ byte-identical behavior to the plain search page.
export const parseSearchQuery = (query, pageDefaults = null) => {
    const incluir = query.incluir;
    const includeMode = incluir === "propiedades" || incluir === "desarrollos" ? incluir : "both";

    const parsed = {
        type: query.tipo
            ? String(query.tipo).split(",").filter(Boolean)
            : (pageDefaults?.type ?? []),
        // No operacion param → the page's preset if any, else default Comprar
        // (["SALE"]). The "todas" sentinel → explicit Todas pick → [].
        // Otherwise treat the URL value as a single op (the segmented control
        // is single-select).
        operation: (() => {
            if (!query.operacion) return pageDefaults?.operation ?? ["SALE"];
            const raw = String(query.operacion);
            if (raw === "todas") return [];
            return [raw];
        })(),
        price: parseRange(query.precio),
        groundSize: parseRange(query.superficie),
        bedrooms: parseSingleOrRange(query.recamaras),
        bathrooms: parseSingleOrRange(query.banos),
        halfBathrooms: parseSingleOrRange(query.mediosBanos),
        parkingLots: parseSingleOrRange(query.estacionamientos),
        q: query.q ? String(query.q) : "",
        latitude: query.lat && Number.isFinite(Number(query.lat)) ? Number(query.lat) : null,
        longitude: query.lng && Number.isFinite(Number(query.lng)) ? Number(query.lng) : null,
        zoom: query.z && Number.isFinite(Number(query.z)) ? Number(query.z) : null,
        order: query.orden ? String(query.orden) : null,
        includeMode,
        city: query.ciudad ? String(query.ciudad) : "",
        neighborhood: query.colonia ? String(query.colonia) : "",
        company: pageDefaults?.company ?? DEFAULT_COMPANY,
        // `radio` round-trips the search radius from URL so shareable links
        // (and the homepage→Buscar route) restore the polygon circle on
        // cold load. Default 5km if not present.
        radius: query.radio && Number.isFinite(Number(query.radio)) ? Number(query.radio) : 5000,
    };
    parsed.polygons = derivePolygons(parsed, query, pageDefaults?.polygons);
    return parsed;
};
